import { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Table, TableHead, TableBody,
  TableRow, TableCell, Chip, Tooltip
} from '@mui/material';
import { Add, Edit, Delete, Warehouse, LocationOn } from '@mui/icons-material';
import { useAlmacenStore } from '../store/almacenStore';
import { almacenService } from '../services/almacenService';
import { type Deposito, type Ubicacion, initialDepositoState, initialUbicacionState } from '../types/almacen.types';

const AlmacenesPage = () => {
  const { depositos, ubicaciones, setDepositos, setUbicaciones, setLoading } = useAlmacenStore();
  const [openDeposito, setOpenDeposito] = useState(false);
  const [openUbicacion, setOpenUbicacion] = useState(false);
  const [selectedDeposito, setSelectedDeposito] = useState<Deposito | null>(null);
  const [formDeposito, setFormDeposito] = useState<Deposito>(initialDepositoState);
  const [formUbicacion, setFormUbicacion] = useState<Ubicacion>(initialUbicacionState);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await almacenService.getAll();
      setDepositos(data);
    } finally {
      setLoading(false);
    }
  };

  const loadUbicaciones = async (deposito: Deposito) => {
    setSelectedDeposito(deposito);
    try {
      const data = await almacenService.getUbicaciones(deposito.idDeposito);
      setUbicaciones(data);
    } catch { setUbicaciones([]); }
  };

  const handleSaveDeposito = async () => {
    try {
      await almacenService.save(formDeposito);
      setOpenDeposito(false);
      await loadData();
    } catch { /* errorService handles */ }
  };

  const handleDeleteDeposito = async (id: number) => {
    if (!confirm('¿Eliminar este almacén?')) return;
    try {
      await almacenService.delete(id);
      await loadData();
      if (selectedDeposito?.idDeposito === id) {
        setSelectedDeposito(null);
        setUbicaciones([]);
      }
    } catch { /* errorService handles */ }
  };

  const handleSaveUbicacion = async () => {
    if (!selectedDeposito) return;
    try {
      await almacenService.saveUbicacion({ ...formUbicacion, idDeposito: selectedDeposito.idDeposito });
      setOpenUbicacion(false);
      await loadUbicaciones(selectedDeposito);
    } catch { /* errorService handles */ }
  };

  const handleDeleteUbicacion = async (id: number) => {
    if (!selectedDeposito || !confirm('¿Eliminar esta ubicación?')) return;
    try {
      await almacenService.deleteUbicacion(id);
      await loadUbicaciones(selectedDeposito);
    } catch { /* errorService handles */ }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Almacenes</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setFormDeposito(initialDepositoState); setOpenDeposito(true); }}>
          Nuevo Almacén
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 3 }}>
        <Paper sx={{ flex: 1, p: 2 }}>
          <Typography variant="h6" gutterBottom><Warehouse sx={{ mr: 1, verticalAlign: 'middle' }} />Almacenes</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Ubicaciones</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {depositos.map((d) => (
                <TableRow key={d.idDeposito} hover selected={selectedDeposito?.idDeposito === d.idDeposito}
                  onClick={() => loadUbicaciones(d)} sx={{ cursor: 'pointer' }}>
                  <TableCell>{d.nombre}</TableCell>
                  <TableCell><Chip label={d.cantidadUbicaciones ?? 0} size="small" /></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setFormDeposito(d); setOpenDeposito(true); }}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteDeposito(d.idDeposito); }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {depositos.length === 0 && (
                <TableRow><TableCell colSpan={3} align="center">No hay almacenes registrados</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Paper sx={{ flex: 1, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6"><LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
              Ubicaciones {selectedDeposito ? `- ${selectedDeposito.nombre}` : ''}
            </Typography>
            {selectedDeposito && (
              <Button size="small" startIcon={<Add />} onClick={() => { setFormUbicacion(initialUbicacionState); setOpenUbicacion(true); }}>
                Agregar
              </Button>
            )}
          </Box>
          {!selectedDeposito ? (
            <Typography color="text.secondary" sx={{ mt: 2 }}>Seleccione un almacén para ver sus ubicaciones</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ubicaciones.map((u) => (
                  <TableRow key={u.idUbicacion}>
                    <TableCell>{u.descripcion}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => handleDeleteUbicacion(u.idUbicacion)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {ubicaciones.length === 0 && (
                  <TableRow><TableCell colSpan={2} align="center">No hay ubicaciones</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>

      {/* Deposito Dialog */}
      <Dialog open={openDeposito} onClose={() => setOpenDeposito(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{formDeposito.idDeposito ? 'Editar' : 'Nuevo'} Almacén</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Nombre" fullWidth value={formDeposito.nombre}
            onChange={(e) => setFormDeposito({ ...formDeposito, nombre: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeposito(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveDeposito}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Ubicacion Dialog */}
      <Dialog open={openUbicacion} onClose={() => setOpenUbicacion(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Ubicación</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Descripción" fullWidth value={formUbicacion.descripcion}
            onChange={(e) => setFormUbicacion({ ...formUbicacion, descripcion: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUbicacion(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveUbicacion}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AlmacenesPage;
